import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Checkbox, Spin, message, Tooltip } from "antd";
import {
  DeleteOutlined,
  UndoOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  CloseOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  PlayCircleOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import {
  getProjectTrashImages,
  restoreProjectImage,
  restoreProjectImagesBatch,
  restoreProjectVideo,
  restoreProjectVideosBatch,
} from "../../../services/projectServices";

export default function ProjectTrashModal({
  visible,
  onClose,
  projectId,
  onRestored,
}) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [trashList, setTrashList] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [restoringKeys, setRestoringKeys] = useState([]);

  // Unique identifier preventing ID collisions between inspection_images and media tables
  const getItemKey = (item) => `${item.media_type || "photo"}_${item.id}`;

  const fetchTrash = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const data = await dispatch(getProjectTrashImages(projectId));
      setTrashList(Array.isArray(data) ? data : []);
      setSelectedKeys([]);
    } catch (err) {
      console.error("Error fetching project trash media:", err);
      message.error("Failed to load trash items");
      setTrashList([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch, projectId]);

  useEffect(() => {
    if (visible && projectId) {
      fetchTrash();
    } else {
      setSelectedKeys([]);
      setRestoringKeys([]);
    }
  }, [visible, projectId, fetchTrash]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedKeys(trashList.map(getItemKey));
    } else {
      setSelectedKeys([]);
    }
  };

  const handleToggleSelect = (item) => {
    const key = getItemKey(item);
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleSingleRestore = async (item) => {
    const key = getItemKey(item);
    setRestoringKeys((prev) => [...prev, key]);
    try {
      if (item.media_type === "video") {
        await dispatch(restoreProjectVideo(item.id));
        message.success("Video restored to project");
      } else {
        await dispatch(restoreProjectImage(projectId, item.id));
        message.success("Photo restored to project gallery");
      }
      setTrashList((prev) => prev.filter((i) => getItemKey(i) !== key));
      setSelectedKeys((prev) => prev.filter((k) => k !== key));
      if (onRestored) onRestored();
      if (onClose) onClose();
    } catch (err) {
      console.error("Error restoring item:", err);
      message.error(item.media_type === "video" ? "Failed to restore video" : "Failed to restore photo");
    } finally {
      setRestoringKeys((prev) => prev.filter((k) => k !== key));
    }
  };

  const handleBatchRestore = async () => {
    if (selectedKeys.length === 0) return;
    setRestoringKeys([...selectedKeys]);
    try {
      const selectedItems = trashList.filter((item) => selectedKeys.includes(getItemKey(item)));
      const photoIds = selectedItems.filter((item) => item.media_type !== "video").map((item) => item.id);
      const videoIds = selectedItems.filter((item) => item.media_type === "video").map((item) => item.id);

      const promises = [];
      if (photoIds.length > 0) {
        promises.push(dispatch(restoreProjectImagesBatch(projectId, photoIds)));
      }
      if (videoIds.length > 0) {
        promises.push(dispatch(restoreProjectVideosBatch(videoIds)));
      }

      await Promise.all(promises);
      const totalCount = selectedKeys.length;
      message.success(`${totalCount} item${totalCount > 1 ? "s" : ""} restored successfully`);
      setTrashList((prev) => prev.filter((item) => !selectedKeys.includes(getItemKey(item))));
      setSelectedKeys([]);
      if (onRestored) onRestored();
      if (onClose) onClose();
    } catch (err) {
      console.error("Error batch restoring items:", err);
      message.error("Failed to restore selected items");
    } finally {
      setRestoringKeys([]);
    }
  };

  const renderLuxuryBadge = (days) => {
    const d = days != null ? Number(days) : 30;
    let badgeClass = "trash-luxury-badge";

    if (d <= 5) {
      badgeClass += " urgent";
    } else if (d <= 14) {
      badgeClass += " warning";
    }

    return (
      <div className={badgeClass}>
        <ClockCircleOutlined className="badge-clock-icon" />
        <span className="badge-text">{d}d left</span>
      </div>
    );
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1120}
      centered
      destroyOnClose
      style={{ top: 12, maxWidth: "calc(100vw - 16px)", margin: "0 auto" }}
      bodyStyle={{ padding: 0 }}
      className="project-trash-modal"
      title={
        <div className="trash-modal-header">
          <div className="trash-header-icon" style={{ background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)", border: "1px solid #a7f3d0", fontSize: 17, display: "flex", alignItems: "center", justifyContent: "center" }}>
            ♻️
          </div>
          <div className="trash-header-text">
            <div className="trash-header-top-line">
              <span className="trash-header-title">Recycle Bin — Deleted Media</span>
              {trashList.length > 0 && (
                <span className="trash-header-count">
                  {trashList.length} {trashList.length === 1 ? "item" : "items"}
                </span>
              )}
            </div>
            <div className="trash-header-sub">
              Photos & videos held for 30 days before permanent deletion. Restore anytime back to project.
            </div>
          </div>
        </div>
      }
    >
      <div style={{ padding: 0 }}>
        {/* Top Control Bar */}
        {trashList.length > 0 && (
          <div className="trash-toolbar">
            <div className="trash-toolbar-left">
              <Checkbox
                checked={
                  trashList.length > 0 && selectedKeys.length === trashList.length
                }
                indeterminate={
                  selectedKeys.length > 0 && selectedKeys.length < trashList.length
                }
                onChange={handleSelectAll}
              >
                <span style={{ fontWeight: 600, color: "#1e293b", fontSize: 13 }}>
                  Select All
                </span>
              </Checkbox>

              {selectedKeys.length > 0 && (
                <span
                  className="trash-selection-chip"
                  onClick={() => setSelectedKeys([])}
                  title="Click to deselect all"
                >
                  <span>{selectedKeys.length} selected</span>
                  <CloseOutlined className="chip-close-icon" />
                </span>
              )}
            </div>

            <div className="trash-toolbar-right">
              <span className="trash-auto-hint">
                <InfoCircleOutlined style={{ color: "#94a3b8" }} />
                Auto-deletes after 30 days
              </span>

              <Button
                type="primary"
                icon={<UndoOutlined />}
                disabled={selectedKeys.length === 0}
                loading={restoringKeys.length > 0 && selectedKeys.length > 0}
                onClick={handleBatchRestore}
                className="trash-batch-restore-btn"
              >
                Restore Selected {selectedKeys.length > 0 ? `(${selectedKeys.length})` : ""}
              </Button>
            </div>
          </div>
        )}

        {/* Content Area */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "70px 0" }}>
            <Spin size="large" />
            <div style={{ marginTop: 14, color: "#64748b", fontSize: 13, fontWeight: 500 }}>
              Loading deleted media...
            </div>
          </div>
        ) : trashList.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px 20px",
              background: "#f8fafc",
              borderRadius: 12,
              border: "1px dashed #cbd5e1",
              margin: "12px 0",
            }}
          >
            <div
              style={{
                width: 58,
                height: 58,
                borderRadius: "50%",
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 26,
                color: "#16a34a",
                marginBottom: 12,
              }}
            >
              <CheckCircleOutlined />
            </div>
            <h4 style={{ margin: "0 0 6px 0", fontSize: 16, color: "#0f172a", fontWeight: 700 }}>
              Recycle Bin is Empty
            </h4>
            <p style={{ margin: 0, color: "#64748b", fontSize: 13 }}>
              No photos or videos have been deleted from this project in the past 30 days.
            </p>
          </div>
        ) : (
          <div className="trash-grid-container">
            <div className="trash-grid">
              {trashList.map((item) => {
                const itemKey = getItemKey(item);
                const isSelected = selectedKeys.includes(itemKey);
                const isRestoring = restoringKeys.includes(itemKey);
                const isVideo = item.media_type === "video";
                const imgThumb =
                  item.thumb_url || item.image_url || item.original_image_url;
                const formattedDate = item.deleted_at
                  ? new Date(item.deleted_at).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })
                  : "Recently";
                const displayTitle =
                  item.description ||
                  item.original_filename ||
                  (isVideo ? `Video #${item.id}` : `Photo #${item.id}`);

                return (
                  <div key={itemKey} className={`trash-card ${isSelected ? "selected" : ""}`}>
                    {/* Thumbnail with luxury badge & optional video overlay */}
                    <div
                      className="trash-thumb-wrapper"
                      onClick={() => handleToggleSelect(item)}
                    >
                      {isVideo ? (
                        <video
                          src={`${item.source_url || imgThumb}#t=0.001`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            pointerEvents: "none",
                            display: "block",
                          }}
                          preload="metadata"
                          muted
                          playsInline
                        />
                      ) : imgThumb ? (
                        <img
                          src={imgThumb}
                          alt={item.original_filename || "Deleted photo"}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="trash-video-placeholder">
                          <span style={{ fontSize: 12, color: "#94a3b8" }}>No Preview</span>
                        </div>
                      )}

                      {/* Video overlay icon & tag */}
                      {isVideo && (
                        <div className="trash-video-overlay">
                          <PlayCircleOutlined className="trash-video-play-icon" />
                          <span className="trash-video-tag">
                            <VideoCameraOutlined style={{ marginRight: 3 }} /> VIDEO
                          </span>
                        </div>
                      )}

                      {/* Checkbox top left */}
                      <div
                        className="trash-check-badge"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleToggleSelect(item)}
                        />
                      </div>

                      {/* Luxury status badge top right */}
                      {renderLuxuryBadge(item.days_remaining)}
                    </div>

                    {/* Card Body */}
                    <div className="trash-card-info">
                      <Tooltip title={displayTitle}>
                        <div className="trash-file-title">
                          {displayTitle}
                        </div>
                      </Tooltip>

                      <div className="trash-file-meta">
                        <CalendarOutlined className="meta-calendar-icon" />
                        <span>Deleted: {formattedDate}</span>
                      </div>

                      {/* Restore Button */}
                      <Button
                        size="small"
                        className="trash-restore-btn"
                        icon={<UndoOutlined />}
                        loading={isRestoring}
                        disabled={restoringKeys.length > 0}
                        onClick={() => handleSingleRestore(item)}
                      >
                        Restore
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
